import { ApolloLink } from '@apollo/client/link';
import {
  BaseHttpLink,
  defaultPrinter,
  fallbackHttpConfig,
  parseAndCheckHttpResponse,
  rewriteURIForGET,
  selectHttpOptionsAndBodyInternal,
  selectURI,
} from '@apollo/client/link/http';
import { filterOperationVariables } from '@apollo/client/link/utils';
import { Observable } from 'rxjs/internal/Observable';

export default class UploadHttpLink extends ApolloLink {
  constructor({
    uri: fetchUri = '/graphql',
    useGETForQueries,
    print = defaultPrinter,
    fetchOptions,
    credentials,
    headers,
    includeExtensions,
    includeUnusedVariables = false,
  }: BaseHttpLink.Options = {}) {
    super(
      (operation) =>
        new Observable((observer) => {
          const context = operation.getContext();
          const { options, body } = selectHttpOptionsAndBodyInternal(
            operation,
            print,
            fallbackHttpConfig,
            {
              http: {
                includeExtensions,
              },
              options: fetchOptions,
              credentials,
              headers,
            },
            {
              http: context.http,
              options: context.fetchOptions,
              credentials: context.credentials,
              headers: context.headers,
            },
          );

          if (body.variables && !includeUnusedVariables)
            body.variables = filterOperationVariables(body.variables, operation.query);

          const { clone, files } = extractFiles(body, '');

          let uri: string = selectURI(operation, fetchUri);

          if (files.size) {
            if (options.headers) {
              // Automatically set by `fetch` when the `body` is a `FormData`
              // instance.
              delete options.headers['content-type'];
              options.headers['Apollo-Require-Preflight'] = 'true';
            }

            // GraphQL multipart request spec:
            // https://github.com/jaydenseric/graphql-multipart-request-spec

            const form = new FormData();

            form.append('operations', JSON.stringify(clone));

            const map: Record<number, string[]> = {};

            let i = 0;
            files.forEach((paths) => {
              map[++i] = paths;
            });
            form.append('map', JSON.stringify(map));

            i = 0;
            files.forEach((_paths, file) => {
              formDataAppendFile(form, String(++i), file);
            });

            options.body = form;
          } else {
            if (
              useGETForQueries &&
              // If the operation contains some mutations GET shouldn’t be used.
              !operation.query.definitions.some(
                (definition) => definition.kind === 'OperationDefinition' && definition.operation === 'mutation',
              )
            )
              options.method = 'GET';

            if (options.method === 'GET') {
              const result =
                /** @type {{ newURI: string } | { parseError: unknown }} */ // The return type is incorrect; `newURI` and `parseError`
                // will never both be present.
                rewriteURIForGET(uri, body);

              if ('parseError' in result) throw result.parseError;

              uri = result.newURI;
            } else options.body = JSON.stringify(clone);
          }

          const controller = new AbortController();
          if (options.signal)
            if (options.signal.aborted) {
              // Respect the user configured abort controller signal.
              controller.abort();
            } else {
              options.signal.addEventListener(
                'abort',
                () => {
                  controller.abort();
                },
                {
                  // Prevent a memory leak if the user configured abort
                  // controller is long lasting, or controls multiple
                  // things.
                  once: true,
                },
              );
            }

          options.signal = controller.signal;

          let cleaningUp: boolean;

          fetch(uri, options)
            .then((response) => {
              // Forward the response on the context.
              operation.setContext({ response });
              return response;
            })
            .then(parseAndCheckHttpResponse(operation))
            .then((result) => {
              observer.next(result);
              observer.complete();
            })
            .catch((error) => {
              // If the observable is being cleaned up, there is no need to call
              // next or error because there are no more subscribers. An error
              // after cleanup begins is likely from the cleanup function
              // aborting the fetch.
              if (!cleaningUp) observer.error(error);
            });

          // Cleanup function.
          return () => {
            cleaningUp = true;

            // Abort fetch. It’s ok to signal an abort even when not fetching.
            if (controller) controller.abort();
          };
        }),
    );
  }
}

function formDataAppendFile(formData: FormData, fieldName: string, file: File | Blob) {
  if ('name' in file) {
    formData.append(fieldName, file, file.name);
  } else {
    formData.append(fieldName, file);
  }
}

interface CloneObject {
  [key: string]: Clone;
}

type Clone = null | null[] | CloneObject | undefined;

function extractFiles(value: any, path: string = '') {
  const clones = new Map<any, Clone>();
  const files = new Map<any, string[]>();

  function recursive(value: any, path: string, recursed: Set<any>) {
    if (isExtractableFile(value)) {
      const filePaths = files.get(value);
      if (filePaths) {
        filePaths.push(path);
      } else {
        files.set(value, [path]);
      }
      return null;
    }

    const valueIsList = Array.isArray(value) || value instanceof FileList;
    const valueIsPlainObject = isPlainObject(value);

    if (valueIsList || valueIsPlainObject) {
      let clone = clones.get(value);
      const uncloned = !clone;

      if (uncloned) {
        clone = valueIsList ? [] : value instanceof Object ? {} : Object.create(null);
        clones.set(value, clone);
      }

      if (!recursed.has(value)) {
        const pathPrefix = path ? `${path}.` : '';
        const recursedDeeper = new Set(recursed).add(value);

        if (valueIsList) {
          let index = 0;
          for (const item of value as any[]) {
            const itemClone = recursive(item, `${pathPrefix}${index++}`, recursedDeeper);
            if (uncloned && Array.isArray(clone)) {
              clone.push(itemClone);
            }
          }
        } else {
          for (const key in value) {
            const propertyClone = recursive(value[key as keyof typeof value], `${pathPrefix}${key}`, recursedDeeper);
            if (uncloned && clone && !Array.isArray(clone)) {
              clone[key] = propertyClone;
            }
          }
        }
      }

      return clone;
    }

    return value;
  }

  return {
    clone: recursive(value, path, new Set()),
    files,
  };
}

function isExtractableFile(value: any) {
  return isFile(value) || isBlob(value);
}

function isFile(value: any): value is File {
  return typeof File !== 'undefined' && value instanceof File;
}

function isBlob(value: any): value is Blob {
  return typeof Blob !== 'undefined' && value instanceof Blob;
}

function isPlainObject(value: any): value is object {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return (
    (prototype === null || prototype === Object.prototype || Object.getPrototypeOf(prototype) === null) &&
    !(Symbol.toStringTag in value) &&
    !(Symbol.iterator in value)
  );
}
